import React, {FC, RefObject, useRef} from 'react';

import {buildReferenceObject} from '../../../../../libs/shared/ui/src/components/reposition/repositionHelpers';
import {SandboxScenariosEnum, StepSlug} from '../../../../helpers/sandbox/sandboxScenarioHelpers';
import {AiAnswersIntroEducationTipCoordinator} from '../educationTips/aiAnswersIntroEducationTipCoordinator';
import {AiAnswersShowcaseAiAnswersEducationTipCoordinator} from '../educationTips/aiAnswersShowcaseAiAnswersEducationTipCoordinator';
import {AiAnswersShowcaseAutomationEducationTipCoordinator} from '../educationTips/aiAnswersShowcaseAutomationEducationTipCoordinator';
import {useSandboxStepIncrement} from '../educationTips/sandboxEducationTipCoordinator';
import {SandboxChatPreview} from './sandboxChatPreview';

/*
 * Props.
 */

interface SandboxChatPreviewCoordinatorProps {
  conversationId: number | undefined;
}

/*
 * Constants.
 */

const stepsV4: ReadonlyArray<string> = Object.freeze([
  'start-scenario-4-conversation-4',
  'showcase-ai-answers',
  'showcase-automation',
]) satisfies ReadonlyArray<StepSlug<SandboxScenariosEnum.SCENARIO4_ACT4>>;

/*
 * Component.
 */

export const SandboxChatPreviewCoordinator: FC<SandboxChatPreviewCoordinatorProps> = (props) => {
  const {conversationId} = props;
  const [, conversationSandbox] = useSandboxStepIncrement(conversationId);

  // Show a preview of the chat widget when we're on the relevant steps.
  const isVisible =
    conversationSandbox?.scenario === SandboxScenariosEnum.SCENARIO4_ACT4 &&
    stepsV4.includes(conversationSandbox?.step) &&
    !conversationSandbox?.completed;

  const windowRef = useRef<HTMLDivElement>(null);
  const messageListRef = useRef<HTMLDivElement>(null);
  const articleRef = useRef<HTMLDivElement>(null);
  const billingMessageReplyRef = useRef<HTMLDivElement>(null);
  const handoffMessageRef = useRef<HTMLDivElement>(null);

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <SandboxChatPreview
        windowRef={windowRef}
        articleRef={articleRef}
        messageListRef={messageListRef}
        billingMessageReplyRef={billingMessageReplyRef}
        handoffMessageRef={handoffMessageRef}
        step={conversationSandbox?.step}
      />
      <AiAnswersIntroEducationTipCoordinator anchor={windowRef} conversationId={conversationId} />
      <AiAnswersShowcaseAiAnswersEducationTipCoordinator
        anchor={{
          getBoundingClientRect: () => getOverlayHighlightRectangle(billingMessageReplyRef, messageListRef),
          contextElement: messageListRef.current ?? undefined,
        }}
        conversationId={conversationId}
      />
      <AiAnswersShowcaseAutomationEducationTipCoordinator
        anchor={{
          getBoundingClientRect: () => getOverlayHighlightRectangle(handoffMessageRef, messageListRef),
          contextElement: messageListRef.current ?? undefined,
        }}
        conversationId={conversationId}
      />
    </>
  );
};

function getOverlayHighlightRectangle(
  anchor: RefObject<HTMLDivElement>,
  viewport: RefObject<HTMLDivElement>,
) {
  // This case should only occur when the education tip is not visible.
  if (!anchor.current || !viewport.current) {
    return new DOMRect(0, 0, 0, 0);
  }

  const anchorRef = buildReferenceObject(anchor);
  const viewportRef = buildReferenceObject(viewport);

  const anchorRect = anchorRef.getBoundingClientRect();
  const viewportRect = viewportRef.getBoundingClientRect();

  const absAnchorHeight = anchorRect.y + anchorRect.height;
  const absViewportHeight = viewportRect.y + viewportRect.height;

  return new DOMRect(
    anchorRect.x,
    anchorRect.y,
    anchorRect.width,
    absAnchorHeight > absViewportHeight
      ? anchorRect.height - (absAnchorHeight - absViewportHeight) - 16
      : anchorRect.height,
  );
}
